import { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faFileLines,
  faDownload,
  faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { toast } from '@/lib/swal';
import { useStepContent } from '../../hooks/useProcedureExecution';
import { useUpdateStep } from '../../hooks/useStepExecution';
import { useProcedureExecutionStore } from '@/stores/procedure-execution.store';
import { StepStatusSelector } from './StepStatusSelector';
import { StepEvidenceSection } from './StepEvidenceSection';
import { StepCommentsSection } from './StepCommentsSection';
import { StepAuthorizationSection } from './StepAuthorizationSection';
import type { StepExecutionView } from '../../services/procedure-execution.service';
import type { StepStatus, PassFailResult } from '../../services/procedure-execution.service';

function extractMsg(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

interface Props {
  inspectionId: string;
  step: StepExecutionView;
  canExecute: boolean;
  isExpanded: boolean;
}

export function StepExecutionBody({ inspectionId, step, canExecute, isExpanded }: Props) {
  const markDirty = useProcedureExecutionStore((s) => s.markDirty);
  const markClean = useProcedureExecutionStore((s) => s.markClean);

  const [localStatus, setLocalStatus] = useState<StepStatus>(step.status);
  const [localPassFail, setLocalPassFail] = useState<PassFailResult | null>(step.passFailResult);
  const [localReason, setLocalReason] = useState(step.reason ?? '');

  const isDirty =
    localStatus !== step.status ||
    localPassFail !== step.passFailResult ||
    localReason !== (step.reason ?? '');

  useEffect(() => {
    if (isDirty) markDirty(step.stepExecutionId);
    else markClean(step.stepExecutionId);
  }, [isDirty, step.stepExecutionId, markDirty, markClean]);

  // Reset local form when server state updates (after successful save)
  useEffect(() => {
    setLocalStatus(step.status);
    setLocalPassFail(step.passFailResult);
    setLocalReason(step.reason ?? '');
  }, [step.status, step.passFailResult, step.reason]);

  const { data: content, isLoading: contentLoading } = useStepContent(
    inspectionId,
    step.stepExecutionId,
    isExpanded,
  );

  const updateMutation = useUpdateStep(inspectionId);

  const needsReason = localStatus === 'SKIPPED' || localStatus === 'NOT_APPLICABLE';
  const needsPassFail = step.passFailRequired && localStatus === 'COMPLETED';

  async function handleSave() {
    if (needsReason && localReason.trim().length < 10) {
      toast.error('Reason must be at least 10 characters');
      return;
    }
    if (needsPassFail && !localPassFail) {
      toast.error('Pass/Fail result is required when completing this step');
      return;
    }

    try {
      await updateMutation.mutateAsync({
        stepExecutionId: step.stepExecutionId,
        dto: {
          status: localStatus,
          passFailResult: needsPassFail ? localPassFail : null,
          reason: needsReason ? localReason.trim() : undefined,
        },
      });
      toast.success('Step saved');
    } catch (e) {
      toast.error(extractMsg(e as Error, 'Failed to save step'));
    }
  }

  function handleCancel() {
    setLocalStatus(step.status);
    setLocalPassFail(step.passFailResult);
    setLocalReason(step.reason ?? '');
    markClean(step.stepExecutionId);
  }

  const saving = updateMutation.isPending;
  const executionLocked = !canExecute;

  return (
    <div className="space-y-4 px-4 pb-4 pt-3">
      {/* Instructions */}
      {contentLoading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <FontAwesomeIcon icon={faSpinner} className="animate-spin h-3 w-3" />
          Loading instructions…
        </div>
      ) : content?.instructionContent ? (
        <div
          className="prose prose-sm max-w-none text-foreground dark:prose-invert [&_a]:text-primary [&_a]:underline"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(content.instructionContent),
          }}
        />
      ) : null}

      {/* Reference Materials */}
      {content && content.referenceMaterials.length > 0 && (
        <div className="space-y-1.5 rounded-lg bg-muted/30 p-3">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            <FontAwesomeIcon icon={faFileLines} className="h-3 w-3" />
            Reference Materials
          </div>
          <div className="space-y-1">
            {content.referenceMaterials.map((m) => (
              <a
                key={m.id}
                href={m.signedUrl}
                download={m.fileName}
                className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-foreground hover:bg-muted transition"
              >
                <FontAwesomeIcon
                  icon={faDownload}
                  className="h-3 w-3 text-muted-foreground shrink-0"
                />
                <span className="truncate">{m.fileName}</span>
                <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                  {(parseInt(m.fileSize, 10) / 1048576).toFixed(1)} MB
                </span>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Authorization */}
      <StepAuthorizationSection
        inspectionId={inspectionId}
        stepExecutionId={step.stepExecutionId}
        authorizationRequired={step.authorizationRequired}
        authorization={step.authorization}
        canExecute={canExecute}
        isExpanded={isExpanded}
      />

      {/* Status Selector */}
      {!executionLocked && (
        <div className="space-y-2">
          <label className="text-xs font-medium text-foreground">Status</label>
          <StepStatusSelector
            value={localStatus}
            onChange={(s) => {
              setLocalStatus(s);
              if (s !== 'SKIPPED' && s !== 'NOT_APPLICABLE') setLocalReason('');
              if (s !== 'COMPLETED') setLocalPassFail(null);
            }}
            disabled={saving}
          />
        </div>
      )}

      {/* Reason Input */}
      {!executionLocked && needsReason && (
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-foreground">
            Reason <span className="text-destructive">*</span>
            <span className="ml-1 text-muted-foreground font-normal">(min 10 chars)</span>
          </label>
          <textarea
            value={localReason}
            onChange={(e) => setLocalReason(e.target.value)}
            placeholder={
              localStatus === 'SKIPPED'
                ? 'Explain why this step was skipped…'
                : 'Explain why this step is not applicable…'
            }
            rows={2}
            maxLength={1000}
            disabled={saving}
            className="w-full resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
          />
        </div>
      )}

      {/* Pass / Fail Toggle */}
      {!executionLocked && needsPassFail && (
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-foreground">
            Result <span className="text-destructive">*</span>
          </label>
          <div className="flex gap-2">
            {(['PASS', 'FAIL'] as PassFailResult[]).map((val) => (
              <button
                key={val}
                type="button"
                onClick={() => setLocalPassFail(val)}
                disabled={saving}
                className={`flex h-[44px] flex-1 items-center justify-center rounded-lg border text-sm font-semibold transition
                  ${
                    localPassFail === val
                      ? val === 'PASS'
                        ? 'border-green-600 bg-green-600/10 text-green-700'
                        : 'border-red-600 bg-red-600/10 text-red-700'
                      : 'border-border text-muted-foreground hover:bg-muted'
                  }
                  ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {val}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Read-only status (OPS / completed execution) */}
      {executionLocked && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <FontAwesomeIcon icon={faInfoCircle} className="h-3 w-3" />
          Read-only view. Status: <span className="font-medium text-foreground">{step.status}</span>
          {step.passFailResult && (
            <span
              className={`ml-1 rounded-full px-2 py-0.5 text-xs font-medium
              ${step.passFailResult === 'PASS' ? 'bg-green-600/10 text-green-700' : 'bg-red-600/10 text-red-700'}`}
            >
              {step.passFailResult}
            </span>
          )}
        </div>
      )}

      {/* Evidence */}
      <StepEvidenceSection
        inspectionId={inspectionId}
        stepExecutionId={step.stepExecutionId}
        evidenceRequired={step.evidenceRequired}
        minPhotos={step.minPhotos}
        minVideos={step.minVideos}
        minDocuments={step.minDocuments}
        canUpload={canExecute}
        isExpanded={isExpanded}
      />

      {/* Comments */}
      <StepCommentsSection
        inspectionId={inspectionId}
        stepExecutionId={step.stepExecutionId}
        commentsAllowed={step.commentsAllowed}
        canPost={canExecute}
        isExpanded={isExpanded}
      />

      {/* Actor info */}
      {step.executedBy && (
        <p className="text-xs text-muted-foreground">
          Last updated by {step.executedBy.fullName}
          {step.executedAt && (
            <>
              {' '}
              ·{' '}
              {new Date(step.executedAt).toLocaleString(undefined, {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </>
          )}
        </p>
      )}

      {/* Save / Cancel */}
      {!executionLocked && isDirty && (
        <div className="flex justify-end gap-2 pt-1 border-t border-border">
          <button
            type="button"
            onClick={handleCancel}
            disabled={saving}
            className="h-[44px] rounded-lg border border-border px-4 text-sm text-foreground transition hover:bg-muted disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving}
            className="flex h-[44px] items-center gap-2 rounded-lg bg-primary px-5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-50"
          >
            {saving && <FontAwesomeIcon icon={faSpinner} className="h-3.5 w-3.5 animate-spin" />}
            Save Step
          </button>
        </div>
      )}
    </div>
  );
}
